import { ProTable, ProTableProps } from '@ant-design/pro-table';
import { useLocalStorageState } from 'ahooks';
import { message, Modal } from 'antd';
import { Key } from 'antd/lib/table/interface';
import classnames from 'classnames';
import { isEmpty } from 'lodash';
import React, { CSSProperties, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppConfig } from '@/config/context';
import { exportCSV } from '@/infrastructure/export-csv';
import { getParamFromUrl } from '@/infrastructure/history';
import {
  getSortType,
  PageSetting,
} from '@/infrastructure/page-sort';
import { getProductId } from '@/infrastructure/product-info';
import { CUSTOM_ACTIONS } from '@/infrastructure/reporter/datong/constant';
import { reportClick } from '@/infrastructure/reporter/datong/report';
import { TABLE_SETTING_CONFIG } from '@/infrastructure/table-config/setting';
import { RawTotalCount } from '@/presentation/components/exception-issue-list/api';
import { IssuesListHeader } from '@/presentation/components/issue-list-header';
import { compareFullDataLoadingText } from '@/presentation/components/issue-list/components/tips';
import { getExport, PAGE_SIZES } from '@/presentation/components/issue-list/utils';
import { BatchOperate } from '@/presentation/components/issue-table/batch-operate';
import { TagBaseType, TagSubType } from '@/presentation/components/question-tag/api';
import { TagStatisticalDistribution } from '@/presentation/components/tag-statistical-distribution';
import { releaseTapdIssue } from '@/presentation/components/tapd-info/api';
import {
  IssueStatus as IssueStatusField,
  Option,
  Zone,
} from '@/proto/field';
import { IssueType } from '@/proto/issue';
import { MetricType } from '@/proto/metric';
import { toLineString } from '@raft/utils/src/case/snake-case';
import style from './index.module.scss';

/**
 * 问题列表组件
 */

type IProps<T> = {
  pgId: string;
  name: string;
  info?: string;
  rowKey: string;
  data: T[];
  total: number;
  type: Zone;
  fetch?: boolean | string;
  formatColumn: (updateProblemList: () => Promise<void>, totalCount?: RawTotalCount) => any;
  loadData: (pageSetting: PageSetting) => Promise<void>;
  getIssueUrl: (data: T) => string;
  batchMode: {
    status?: { options: Option[] },
    tapd?: boolean,
    export?: boolean,
    cancel?: boolean,
    tapdStatus?: boolean,
    tapdHandler?: boolean,
    enableBatchTag?: {
      baseType: TagBaseType,
      subType: TagSubType,
    },
    enableBatchTrend?: boolean
    token?: string,
    metricType?: MetricType,
  };
  toolbarActions?: React.ReactNode[];
  // 是否对比全量数据
  isCompareFullData?: boolean;
  className?: string;
  xlsxName?: string;
  headerTitle?: boolean;
  tableProps?: ProTableProps<any, any>;
  totalCount?: RawTotalCount;
  isTag?: boolean;
  issueType?: IssueType;
  totalDocCount?: number;
  bodyStyle?: CSSProperties;
  dependencyList?: any[];
  getDetailsUrl?: (record: Record<string, any>) => string;
  token?: string;
  metricType?: MetricType;
  fields?: Record<string, any>;
};

export type RecordData = Record<string, any>;


export const IssueList = <T extends Record<string, any>>(props: IProps<T>) => {
  const { className,
    headerTitle = true,
    batchMode,
    tableProps,
    pgId,
    isTag,
    issueType,
    dependencyList = [],
    getDetailsUrl } = props;
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const config = useAppConfig();
  const enableCancel = props.batchMode?.cancel ?? config.enableBatchCancel;
  const [localPageSize, setLocalPageSize] = useLocalStorageState<number | undefined>(
    `table-page-size-${pgId}`,
    {
      defaultValue: 50,
    },
  );
  const [pageSetting, setPageSetting] = useState<PageSetting>({
    page: 1,
    pageSize: localPageSize && PAGE_SIZES.includes(localPageSize) ? localPageSize : 50,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<T[]>([]);
  // 查询全量数据耗时，单位: s
  const [fullDataCost, setFullDataCost] = useState(0);
  const maxFullDataCost = 15;

  useEffect(() => {
    if (props.fetch !== undefined) {
      // 查询重置为第一页
      const initPageSetting = {
        ...pageSetting,
        page: 1,
      };
      setPageSetting(initPageSetting);
      load(initPageSetting);
    }
  }, [props.fetch]);

  useEffect(() => {
    setFullDataCost(0);
    if (loading && props?.isCompareFullData) {
      let fullDataCost = 0;
      const timer = setInterval(() => {
        fullDataCost += 1;
        setFullDataCost(fullDataCost);
        if (fullDataCost >= maxFullDataCost) {
          clearInterval(timer);
        }
      }, 1000);
    }
  }, [loading]);

  const tip = props?.isCompareFullData && fullDataCost >= maxFullDataCost ? compareFullDataLoadingText : null;

  const load = async (changePageSetting?: PageSetting) => {
    setLoading(true);
    setSelectedRowKeys([]);
    setSelectedRows([]);
    await props.loadData(changePageSetting ?? pageSetting);
    setLoading(false);
  };

  const columns = props.formatColumn(load, props.totalCount);
  const handleTableChange = (
    pagination: any,
    _filters: object,
    sorter: any,
    extra: {
      action: 'paginate' | 'sort' | 'filter';
    },
  ) => {
    const sortField = toLineString(sorter.field ?? '');
    const sortType = getSortType(sorter.order);
    const sortAction = `common_sort_list-${sortField}`;
    const changePageSetting = {
      page: pagination.current,
      pageSize: pagination.pageSize,
      sortField,
      sortType,
    };

    setPageSetting(changePageSetting);
    load(changePageSetting);
    setSelectedRowKeys([]);
    setSelectedRows([]);
    if (extra.action === 'paginate') {
      reportClick(props.pgId, CUSTOM_ACTIONS.commonSwitchPage, {
        from: props.pgId,
      });
    }
    if (extra.action === 'sort') {
      reportClick(props.pgId, sortAction, { from: props.pgId });
      reportClick(props.pgId, CUSTOM_ACTIONS.commonChangeCollation, {
        from: props.pgId,
        param3: sortField,
        param4: sortType,
      });
    }
  };

  const onDownload = async () => {
    const exportData = getExport(props.type, props.data, columns, props.totalCount, getDetailsUrl);
    const filterName = props.xlsxName || props.name.replace(/[\\/?*[\]]/g, '');
    await exportCSV(filterName, [exportData.head], exportData.body);
  };

  const onBatchCancel = async () => {
    Modal.confirm({
      title: t('取消关联确认'),
      content: t('确认要将以下N个问题的TAPD状态取消关联吗？'),
      onOk: async () => {
        message.info({
          content: t('批量取消关联中...'),
          duration: 0,
        });

        // 过滤不需要取消的issue
        const filteredIssues = selectedRowKeys.filter((feature: React.Key) => {
          const currentIssue = props.data.find(data => data.feature === feature);
          return !!currentIssue?.tapdUrl;
        });

        await Promise.all(filteredIssues.map((issueKey: React.Key) => releaseTapdIssue({
          productId: getProductId(),
          type: props.type,
          issueKey: issueKey as string,
        })));

        message.destroy();
        message.success(t('批量取消成功'));
        load();
      },
    });
  };

  const toolbarRender = () => {
    const actions = [];
    if (props.toolbarActions) {
      actions.push(props.toolbarActions);
    }
    if (isTag) {
      actions.push(<TagStatisticalDistribution issueType={issueType} token={getParamFromUrl('token')}/>);
    }
    actions.push(<BatchOperate
        type={props.type}
        rows={selectedRows}
        onSubmit={load}
        headToken={batchMode.token}
        disabled={selectedRows.length === 0}
        getIssueUrl={props.getIssueUrl!}
        pageId={props.pgId}
        onExport={onDownload}
        enableBatchCreateTapd={props.batchMode.tapd}
        enableBatchExport={props.batchMode.export}
        enableBatchStatus={!!props.batchMode.status}
        options={props.batchMode.status?.options}
        enableBatchTapdStatus={props.batchMode?.tapdStatus}
        enableBatchCancel={enableCancel}
        onCancel={onBatchCancel}
        enableBatchTapdHandler={props.batchMode.tapdHandler}
        enableBatchTag={props.batchMode.enableBatchTag}
        metricType={batchMode.metricType}
      />);

    return actions;
  };

  // 持久化pagesize
  const pageSizeChange = (_page: number, pageSize: number) => {
    setLocalPageSize(pageSize);
  };

  useEffect(() => {
    if (!isEmpty(dependencyList)) {
      load();
    }
  }, dependencyList);

  return (
    <>
      <ProTable
        cardProps={{
          bodyStyle: props.bodyStyle,
        }}
        className={classnames(style.table, className)}
        rowClassName={record => (record.issueStatus === IssueStatusField.Recovered ? style.rowRecovered : '')}
        headerTitle={headerTitle ? (
          <IssuesListHeader
            name={props.name}
            info={props.info}
          />
        ) : undefined}
        toolBarRender={toolbarRender}
        columnsState={{
          persistenceKey: `table-setting-${props.pgId}-v2`,
          persistenceType: 'localStorage',
          onChange(value) {
            reportClick(props.pgId, CUSTOM_ACTIONS.tableSettingColumnsChange, {
              param3: JSON.stringify(value),
            });
          },
        }}
        options={{
          density: false,
          fullScreen: false,
          reload: false,
          setting: TABLE_SETTING_CONFIG,
        }}
        search={false}
        columns={columns}
        dataSource={props.data}
        loading={{ spinning: loading, tip }}
        pagination={{
          total: props.total,
          current: pageSetting.page,
          pageSize: pageSetting.pageSize,
          showSizeChanger: true,
          showTotal: (total, range) => t('第 {{start}}-{{end}} 条/总共 {{total}} 条', { start: range[0], end: range[1], total }),
          locale: {
            items_per_page: t('条/页'),
          },
          onChange: pageSizeChange,
        }}
        rowSelection={props.batchMode ? {
          selectedRowKeys,
          onChange: (selectedRowKeys: Key[], selectedRows: T[]) => {
            setSelectedRowKeys(selectedRowKeys);
            setSelectedRows(selectedRows);
          },
        } : undefined}
        rowKey={props.rowKey}
        scroll={{ x: 1400 }}
        sortDirections={['descend', 'ascend']}
        onChange={handleTableChange}
        {...tableProps}
      />
    </>
  );
};

export const getTotal = (topNum: number | undefined, total: number) => (topNum && topNum < total ? topNum : total);
